"use client"

import { useScroll } from "ahooks"
import { useEffect, useMemo, useState } from "react"

import type { AffixWrapperProps } from "../type"
import styles from "./lindex.module.scss"

export default function AffixWrapper({
  visibleOffset = 1800,
  baseWidth = 1380,
  bottom = 32,
  unionIconLeft = 20,
  unionIconRight = 20,
  offsetRight = 20,
  hideBelowWidth = 768,
  footerHeight = 434,
  positionMode = "fixed",
  fixedRight = 20,
  children,
}: AffixWrapperProps) {
  const [mounted, setMounted] = useState(false)
  const [windowWidth, setWindowWidth] = useState(0)
  const scroll = useScroll(mounted ? document : null)

  const unionIconWidth = 40

  useEffect(() => {
    setMounted(true)
    setWindowWidth(window.innerWidth)

    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const visible = useMemo(() => {
    return (scroll?.top ?? 0) > visibleOffset
  }, [scroll, visibleOffset])

  const distBottom = useMemo(() => {
    if (!mounted) return 0
    const scrollHeight = document.documentElement.scrollHeight
    const clientHeight = window.innerHeight
    return scrollHeight - (scroll?.top ?? 0) - clientHeight + bottom
  }, [bottom, mounted, scroll])

  const right = useMemo(() => {
    if (!mounted) return 20

    if (positionMode === "fixed") {
      return fixedRight
    }

    if (windowWidth > baseWidth + (unionIconLeft + unionIconWidth + unionIconRight + offsetRight) * 2) {
      return (windowWidth - baseWidth) / 2 - unionIconLeft - unionIconWidth
    }
    return 20
  }, [baseWidth, fixedRight, mounted, offsetRight, positionMode, unionIconLeft, unionIconRight, windowWidth])

  if (!mounted) return null
  if (windowWidth < hideBelowWidth) return null
  if (!visible) return null

  const distBottomRounded = Math.round(distBottom)

  return (
    <div className={styles.affixWrapper} style={{ right, bottom }} data-dist-bottom={distBottomRounded}>
      {children}
    </div>
  )
}
